import React, { useEffect, useState } from 'react';
import { Upload, FileSpreadsheet, Trash2, Eye, X } from 'lucide-react';
import { can } from '../permissions';
import toast from 'react-hot-toast';
import api from '../api';

const miniBtn = { padding: '4px 9px', background: '#fff', border: '1px solid #fbbf24',
  borderRadius: 6, fontSize: 11, fontWeight: 600, color: '#92400e', cursor: 'pointer' };
const pillOn = { padding: '4px 10px', background: '#92400e', border: '1px solid #92400e',
  borderRadius: 20, fontSize: 11, fontWeight: 600, color: '#fff', cursor: 'pointer' };
const pillOff = { padding: '4px 10px', background: '#fff', border: '1px solid #fbbf24',
  borderRadius: 20, fontSize: 11, fontWeight: 600, color: '#a16207', cursor: 'pointer' };

export default function Invoices() {
  const [drivers, setDrivers] = useState([]);
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [reportDriver, setReportDriver] = useState('all');
  const [downloading, setDownloading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [file, setFile] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [assignments, setAssignments] = useState({});
  const [salesmen, setSalesmen] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [viewSession, setViewSession] = useState(null);
  const [sessionInvoices, setSessionInvoices] = useState([]);
  const [rescheduleModal, setRescheduleModal] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [timeModal, setTimeModal] = useState(null);
  const [newStartedAt, setNewStartedAt] = useState('');
  const [newClosedAt, setNewClosedAt] = useState('');
  const [timeReason, setTimeReason] = useState('');
  const [odoModal, setOdoModal] = useState(null);
  const [odoStart, setOdoStart] = useState('');
  const [odoEnd, setOdoEnd] = useState('');
  const [bcOpen, setBcOpen] = useState(false);
  const [bcStep, setBcStep] = useState('date');
  const [bcFrom, setBcFrom] = useState(new Date().toISOString().split('T')[0]);
  const [bcTo, setBcTo] = useState(new Date().toISOString().split('T')[0]);
  const [bcRange, setBcRange] = useState(false);
  const [bcPlanDate, setBcPlanDate] = useState(new Date().toISOString().split('T')[0]);
  const [bcLoading, setBcLoading] = useState(false);
  const [bcSalespeople, setBcSalespeople] = useState([]);
  const [bcAssignments, setBcAssignments] = useState({});
  const [bcExpanded, setBcExpanded] = useState(null);
  const [bcExpandLoading, setBcExpandLoading] = useState(false);
  const [bcInvoices, setBcInvoices] = useState({});
  const [bcExcluded, setBcExcluded] = useState({});
  const [bcNeedsLocation, setBcNeedsLocation] = useState([]);
  const [bcCoords, setBcCoords] = useState({});

  const load = () => {
    api.get('/drivers').then(r => setDrivers(r.data));
    api.get('/invoices/sessions').then(r => setSessions(r.data));
  };
  useEffect(() => { load(); }, []);

  const [conflicts, setConflicts] = useState(null);   // null = not checked yet
  const [choices, setChoices] = useState({});         // invoice_no -> 'replace' | 'skip'
  const [checking, setChecking] = useState(false);

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setConflicts(null);
    setChoices({});
    const reader = new FileReader();
    reader.onload = (evt) => {
      const xlsx = require('xlsx');
      const wb = xlsx.read(evt.target.result, { type: 'binary' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = xlsx.utils.sheet_to_json(ws);
      setTotalRows(data.length);
      setPreview(data.slice(0, 5));
      const names = [...new Set(data.map(r =>
        r['Salesperson Code'] || r['salesperson_code'] || r['Salesman'] || ''
      ).filter(Boolean))];
      setSalesmen(names);
      toast.success(`${data.length} invoices loaded, ${names.length} salesmen found`);
      checkForConflicts(f);
    };
    reader.readAsBinaryString(f);
  };

  // Ask the server whether any of these invoice numbers already exist before
  // anything is written. A clean file goes straight through as before.
  const checkForConflicts = async (f) => {
    setChecking(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post('/invoices/check-upload', fd);
      if (data.delivered.length || data.open.length || data.duplicates_in_file.length) {
        setConflicts(data);
        // default every open conflict to keeping what is already there
        const d = {};
        data.open.forEach(o => { d[o.invoice_no] = 'skip'; });
        setChoices(d);
      } else {
        setConflicts(null);
      }
    } catch (e) {
      // If the check fails the upload can still proceed; it re-checks server side.
      setConflicts(null);
    } finally { setChecking(false); }
  };

  const setAll = (what) => {
    if (!conflicts) return;
    const d = {};
    conflicts.open.forEach(o => { d[o.invoice_no] = what; });
    setChoices(d);
  };

  const upload = async () => {
    if (!file) return toast.error('Please select a file');
    if (!Object.keys(assignments).length) return toast.error('Assign at least one salesman to a driver');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('session_date', date);
      fd.append('driver_assignments', JSON.stringify(assignments));
      fd.append('decisions', JSON.stringify(choices));
      const { data } = await api.post('/invoices/upload', fd);
      const sk = data.skipped || { delivered: [], chosen: [] };
      const n = (sk.delivered.length || 0) + (sk.chosen.length || 0);
      if (n) {
        toast.success(`Uploaded. ${n} invoice${n === 1 ? '' : 's'} skipped as already in the system.`);
      } else {
        toast.success('Invoices uploaded and assigned');
      }
      setFile(null); setSalesmen([]); setPreview([]); setAssignments({}); setTotalRows(0);
      setConflicts(null); setChoices({});
      load();
    } catch { toast.error('Upload failed'); }
    finally { setLoading(false); }
  };
  const bcFindSalespeople = async () => {
    setBcLoading(true);
    try {
      const { data } = await api.get('/api/bc/salespeople', { params: { from: bcFrom, to: bcRange ? bcTo : bcFrom } });
      if (!data.total) { toast.error('No Business Central invoices found for that date'); return; }
      setBcSalespeople(data.salespeople);
      setBcAssignments({});
      setBcExpanded(null);
      setBcInvoices({});
      setBcExcluded({});
      setBcStep('mapping');
    } catch (e) { toast.error(e.response?.data?.error || 'Could not reach Business Central'); }
    finally { setBcLoading(false); }
  };
  const bcToggleExpand = async (sp) => {
    if (bcExpanded === sp) { setBcExpanded(null); return; }
    setBcExpanded(sp);
    if (bcInvoices[sp]) return;
    setBcExpandLoading(true);
    try {
      const { data } = await api.get('/api/bc/preview', {
        params: { from: bcFrom, to: bcRange ? bcTo : bcFrom, salespeople: sp } });
      const rows = [
        ...data.ready.map(r => ({ ...r, state: 'ready' })),
        ...data.missing_coordinates.map(r => ({ ...r, state: 'missing_coords' })),
        ...data.already_here.map(r => ({ ...r, state: 'already_here' })),
      ];
      setBcInvoices({ ...bcInvoices, [sp]: rows });
    } catch (e) { toast.error(e.response?.data?.error || 'Could not load invoices'); }
    finally { setBcExpandLoading(false); }
  };
  const bcToggleInvoice = (invoiceNo) => {
    setBcExcluded({ ...bcExcluded, [invoiceNo]: !bcExcluded[invoiceNo] });
  };
  const bcToggleAllForSalesperson = (sp, select) => {
    const invs = bcInvoices[sp] || [];
    const updates = { ...bcExcluded };
    invs.forEach(inv => { if (inv.state !== 'already_here') updates[inv.invoice_no] = !select; });
    setBcExcluded(updates);
  };
  const bcImport = async () => {
    if (!Object.keys(bcAssignments).length) return toast.error('Assign at least one salesperson to a driver');
    setBcLoading(true);
    const payload = {};
    Object.entries(bcAssignments).forEach(([sp, id]) => { payload[sp === '(none)' ? '' : sp] = id; });
    const exclude_invoices = Object.keys(bcExcluded).filter(k => bcExcluded[k]);
    try {
      const { data } = await api.post('/api/bc/import', {
        from: bcFrom, to: bcRange ? bcTo : bcFrom, session_date: bcPlanDate,
        driver_assignments: payload, exclude_invoices });
      const skippedN = (data.skipped || []).length;
      toast.success(`${data.imported} invoice${data.imported === 1 ? '' : 's'} imported${skippedN ? `, ${skippedN} skipped` : ''}`);
      if (data.needs_location && data.needs_location.length) {
        setBcNeedsLocation(data.needs_location);
        setBcCoords({});
        setBcStep('coords');
      } else {
        setBcOpen(false);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Import failed'); }
    finally { setBcLoading(false); }
  };
  const bcSaveLocation = async (cust) => {
    const c = bcCoords[cust.customer_code] || {};
    if (!c.lat || !c.lng) return toast.error('Enter both latitude and longitude');
    try {
      await api.post('/api/bc/customer-location', {
        customer_code: cust.customer_code, customer_name: cust.customer_name,
        latitude: c.lat, longitude: c.lng });
      toast.success(`${cust.customer_name} saved`);
      setBcNeedsLocation(bcNeedsLocation.filter(x => x.customer_code !== cust.customer_code));
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Could not save location'); }
  };

  const deleteSession = async (id, driverName) => {
    if (!window.confirm(`Delete all invoices for ${driverName}? This cannot be undone.`)) return;
    try {
      await api.delete('/deliveries/session/' + id);
      toast.success('Session deleted');
      load();
    } catch { toast.error('Error deleting session'); }
  };

  const viewInvoices = async (session) => {
    setViewSession(session);
    const r = await api.get('/deliveries/session/' + session.id);
    setSessionInvoices(r.data);
  };

  const openOdoEdit = (s) => {
    setOdoModal(s);
    setOdoStart(s.odo_start || '');
    setOdoEnd(s.odo_end || '');
  };

  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openTimeModal = (s) => {
    setTimeModal(s);
    setNewStartedAt(toLocalInput(s.started_at));
    setNewClosedAt(toLocalInput(s.route_closed_at || s.completed_at));
    setTimeReason('');
  };

  const submitTimeChange = async () => {
    if (!timeReason.trim()) { toast.error('Please enter a reason'); return; }
    if (!newStartedAt || !newClosedAt) { toast.error('Both times are required'); return; }
    try {
      await api.post('/deliveries/adjust-session-time/' + timeModal.id, {
        started_at: new Date(newStartedAt).toISOString(),
        closed_at: new Date(newClosedAt).toISOString(),
        reason: timeReason.trim(),
      });
      toast.success('Session time updated');
      setTimeModal(null);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to update time'); }
  };

  const saveOdo = async () => {
    try {
      await api.post('/deliveries/update-odo/' + odoModal.id, {
        odo_start: odoStart ? parseInt(odoStart) : undefined,
        odo_end: odoEnd ? parseInt(odoEnd) : undefined
      });
      toast.success('Odometer updated');
      setOdoModal(null);
      load();
    } catch { toast.error('Error saving'); }
  };

  const openAdminReschedule = (inv) => {
    setRescheduleModal(inv);
    setRescheduleDate(new Date(Date.now() + 86400000).toISOString().split('T')[0]);
  };

  const confirmAdminReschedule = async () => {
    if (!rescheduleDate) return toast.error('Pick a date');
    try {
      await api.post('/invoices/admin-reschedule/' + rescheduleModal.id, { reschedule_date: rescheduleDate });
      toast.success('Invoice rescheduled — will appear when you upload for that date');
      setRescheduleModal(null);
      if (viewSession) {
        const r = await api.get('/deliveries/session/' + viewSession.id);
        setSessionInvoices(r.data);
      }
    } catch { toast.error('Error rescheduling'); }
  };

  const forceCloseSession = async (id) => {
    if (!window.confirm('Force-close this session? Use this if a driver session is stuck "In Progress" from a previous day.')) return;
    const odoInput = window.prompt('Enter the closing odometer reading for this session:');
    if (odoInput === null) return;
    const odo_end = Number(odoInput);
    if (!odoInput.trim() || Number.isNaN(odo_end)) {
      toast.error('A valid odometer number is required to force-close');
      return;
    }
    try {
      await api.post('/deliveries/force-close/' + id, { odo_end });
      toast.success('Session closed');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Error closing session'); }
  };
  const reoptimizeSession = async (id) => {
    if (!window.confirm('Re-optimize the driver\'s REMAINING stops from their current location?\n\nTell the driver to check their app — their stop order will change.')) return;
    try {
      const r = await api.post('/api/routes/reoptimize/' + id, {});
      toast.success(`Re-optimized ${r.data.reoptimized} remaining stops (from ${r.data.started_from === 'driver_gps' ? 'driver location' : 'depot'})`);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Re-optimize failed'); }
  };
  const downloadDaily = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/invoices/export-daily?date=${reportDate}&driver_id=${reportDriver}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily-report-${reportDate}${reportDriver !== 'all' ? '-driver' : ''}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch { toast.error('Download failed'); }
    finally { setDownloading(false); }
  };
  const runningSessionFor = (s) => sessions.find(x =>
    x.status === 'in_progress' &&
    x.driver_name === s.driver_name &&
    new Date(x.session_date).toDateString() === new Date(s.session_date).toDateString()
  );
  const mergeIntoRunning = async (source) => {
    const target = runningSessionFor(source);
    if (!target) { toast.error('No running route found for this driver today'); return; }
    if (!window.confirm(`Move ${source.driver_name}'s assigned invoices into their running route?\n\nAfter moving you should re-optimize so the new stops fit the route.`)) return;
    try {
      const r = await api.post('/deliveries/move-to-session', { source_session_id: source.id, target_session_id: target.id });
      toast.success(`Moved ${r.data.moved} invoice(s) into the running route`);
      if (window.confirm('Invoices moved. Re-optimize the running route now?\n\nTell the driver their stop order will change.')) {
        const ro = await api.post('/api/routes/reoptimize/' + target.id, {});
        toast.success(`Re-optimized ${ro.data.reoptimized} remaining stops`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Merge failed'); }
  };

  const statusBadge = (status) => {
    const map = {
      pending: { bg: '#fef9c3', color: '#854d0e', label: 'Pending' },
      assigned: { bg: '#dbeafe', color: '#1d4ed8', label: 'Assigned' },
      in_progress: { bg: '#e0f2fe', color: '#0369a1', label: 'In Progress' },
      completed: { bg: '#dcfce7', color: '#16a34a', label: 'Completed' },
      route_planned: { bg: '#f3e8ff', color: '#7c3aed', label: 'Route Planned' },
    };
    const s = map[status] || { bg: '#f1f5f9', color: '#475569', label: status };
    return <span style={{ padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: s.bg, color: s.color }}>{s.label}</span>;
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', margin: '0 0 4px' }}>Invoice Management</h1>
      <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 16px' }}>Upload, assign and manage delivery invoices</p>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#fff', borderRadius: 12, padding: '12px 16px', marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>📅 Daily Closing Report</span>
        <input type="date" value={reportDate} max={new Date().toISOString().split('T')[0]} onChange={e => setReportDate(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }} />
        <select value={reportDriver} onChange={e => setReportDriver(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13 }}>
          <option value="all">All drivers</option>
          {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <button onClick={downloadDaily} disabled={downloading}
          style={{ padding: '8px 16px', background: '#16a34a', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: downloading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <FileSpreadsheet size={15} /> {downloading ? 'Preparing...' : 'Download Excel'}
        </button>
        {can('invoices','upload') && (
          <button onClick={() => { setBcStep('date'); const t = new Date().toISOString().split('T')[0]; setBcFrom(t); setBcTo(t); setBcRange(false); setBcPlanDate(t); setBcOpen(true); }}
            style={{ padding: '8px 16px', background: '#0369a1', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Upload size={15} /> Pull from Business Central
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24 }}>
        {/* Upload */}
        {can('invoices','upload') && <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '0 0 16px' }}>Upload Excel File</h2>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 5 }}>Delivery date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <label style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 24,
            border: '2px dashed #cbd5e1', borderRadius: 10, cursor: 'pointer',
            background: file ? '#f0fdf4' : '#f8fafc', marginBottom: 16
          }}>
            <input type="file" accept=".xlsx,.xls" onChange={handleFile} style={{ display: 'none' }} />
            <FileSpreadsheet size={32} color={file ? '#16a34a' : '#94a3b8'} style={{ marginBottom: 8 }} />
            <span style={{ fontSize: 13, color: file ? '#16a34a' : '#64748b', fontWeight: 500 }}>
              {file ? `✓ ${file.name}` : 'Click to select Excel file'}
            </span>
            {totalRows > 0 && <span style={{ fontSize: 12, color: '#16a34a', marginTop: 4 }}>{totalRows} invoices loaded</span>}
          </label>

          {salesmen.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                Assign Salesperson → Driver
              </div>
              {salesmen.map(s => (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1, fontSize: 13, color: '#1e293b', fontWeight: 500, padding: '6px 10px', background: '#f1f5f9', borderRadius: 6 }}>{s}</div>
                  <span style={{ color: '#94a3b8' }}>→</span>
                  <select value={assignments[s] || ''} onChange={e => setAssignments({...assignments, [s]: e.target.value})}
                    style={{ flex: 1, padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12 }}>
                    <option value="">Select driver</option>
                    {drivers.map(d => <option key={d.id} value={d.name}>{d.name}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}

          {checking && (
            <div style={{ padding: '10px 12px', background: '#f1f5f9', borderRadius: 8, fontSize: 12.5,
                          color: '#64748b', marginBottom: 10 }}>
              Checking invoice numbers against the system...
            </div>
          )}

          {conflicts && (
            <div style={{ border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 10,
                          padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#92400e', marginBottom: 3 }}>
                Some invoice numbers are already in the system
              </div>
              <div style={{ fontSize: 12, color: '#a16207', marginBottom: 12 }}>
                {conflicts.ready} of {conflicts.total} rows will upload normally.
              </div>

              {conflicts.duplicates_in_file.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#92400e', marginBottom: 5 }}>
                    Repeated inside your file
                  </div>
                  <div style={{ fontSize: 12, color: '#a16207' }}>
                    {conflicts.duplicates_in_file.map(d => `${d.invoice_no} (${d.times}x)`).join(', ')}
                  </div>
                </div>
              )}

              {conflicts.delivered.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
                    Already delivered, so these will be skipped
                  </div>
                  {conflicts.delivered.map(d => (
                    <div key={d.invoice_no} style={{ display: 'flex', justifyContent: 'space-between',
                         gap: 10, padding: '5px 0', fontSize: 12, color: '#78350f',
                         borderBottom: '1px solid #fde68a' }}>
                      <span><b style={{ fontFamily: 'monospace' }}>{d.invoice_no}</b> {d.customer_name}</span>
                      <span style={{ whiteSpace: 'nowrap', color: '#a16207' }}>
                        {d.delivered_on ? new Date(d.delivered_on).toLocaleDateString('en-GB') : ''}
                        {d.driver_name ? ' by ' + d.driver_name : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {conflicts.open.length > 0 && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: '#92400e' }}>
                      Still open, so choose what to do
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button type="button" onClick={() => setAll('skip')} style={miniBtn}>Keep all existing</button>
                      <button type="button" onClick={() => setAll('replace')} style={miniBtn}>Replace all</button>
                    </div>
                  </div>
                  {conflicts.open.map(o => (
                    <div key={o.invoice_no} style={{ display: 'flex', justifyContent: 'space-between',
                         alignItems: 'center', gap: 10, padding: '7px 0', fontSize: 12,
                         color: '#78350f', borderBottom: '1px solid #fde68a', flexWrap: 'wrap' }}>
                      <span>
                        <b style={{ fontFamily: 'monospace' }}>{o.invoice_no}</b> {o.customer_name}
                        <span style={{ color: '#a16207' }}> ({o.state})</span>
                      </span>
                      <span style={{ display: 'flex', gap: 5 }}>
                        <button type="button" onClick={() => setChoices({ ...choices, [o.invoice_no]: 'skip' })}
                          style={choices[o.invoice_no] === 'skip' ? pillOn : pillOff}>Keep existing</button>
                        <button type="button" onClick={() => setChoices({ ...choices, [o.invoice_no]: 'replace' })}
                          style={choices[o.invoice_no] === 'replace' ? pillOn : pillOff}>Replace</button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <button onClick={upload} disabled={loading || !file}
            style={{ width: '100%', padding: 11, background: file ? '#0284c7' : '#cbd5e1', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: file ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Upload size={15} /> {loading ? 'Uploading...' : 'Upload & Assign'}
          </button>
        </div>}

        {/* Preview */}
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'auto' }}>
          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: '0 0 16px' }}>Preview (first 5 rows)</h2>
          {preview.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr>{Object.keys(preview[0]).map(k => (
                    <th key={k} style={{ padding: '6px 8px', background: '#f8fafc', color: '#64748b', textAlign: 'left', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{k}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i}>{Object.values(row).map((v, j) => (
                      <td key={j} style={{ padding: '6px 8px', color: '#475569', whiteSpace: 'nowrap' }}>{String(v)}</td>
                    ))}</tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', paddingTop: 40 }}>Upload a file to preview</div>
          )}
        </div>
      </div>

      {/* Sessions table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, color: '#1e293b', margin: 0 }}>All Delivery Sessions</h2>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>{sessions.length} sessions</span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Date','Driver','Invoices','Value','KM','Duration','Status','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 16px', textAlign: 'left', color: '#64748b', fontWeight: 500, fontSize: 12 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sessions.map(s => (
              <tr key={s.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                <td style={{ padding: '11px 16px', color: '#475569' }}>{new Date(s.session_date).toLocaleDateString()}</td>
                <td style={{ padding: '11px 16px', fontWeight: 500, color: '#1e293b' }}>{s.driver_name}</td>
                <td style={{ padding: '11px 16px', color: '#475569' }}>{s.total_invoices}</td>
                <td style={{ padding: '11px 16px', color: '#1e293b' }}>AED {Number(s.total_value||0).toLocaleString()}</td>
                <td style={{ padding: '11px 16px', color: '#475569' }}>{Number(s.actual_km||0).toFixed(1)} km</td>
                <td style={{ padding: '11px 16px', color: '#475569' }}>
                  {s.actual_duration ? `${s.actual_duration} min` : s.started_at ? '⏱ Running' : '—'}
                </td>
                <td style={{ padding: '11px 16px' }}>{statusBadge(s.status)}</td>
                <td style={{ padding: '11px 16px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => viewInvoices(s)}
                      style={{ padding: '5px 10px', background: '#eff6ff', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#0284c7', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Eye size={13} /> View
                    </button>
                    {can('invoices','delete') && ['pending','route_planned','assigned'].includes(s.status) && (
                      <button onClick={() => deleteSession(s.id, s.driver_name)}
                        style={{ padding: '5px 10px', background: '#fef2f2', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#dc2626', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Trash2 size={13} /> Delete
                      </button>
                    )}
                    {['pending','route_planned','assigned'].includes(s.status) && can('route_planner','assign') && runningSessionFor(s) && (
                      <button onClick={() => mergeIntoRunning(s)}
                        style={{ padding: '5px 10px', background: '#ecfeff', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#0891b2', fontSize: 12, fontWeight: 600 }}>
                        → Merge into running
                      </button>
                    )}
                    {s.status === 'in_progress' && can('invoices','delete') && (
                      <button onClick={() => forceCloseSession(s.id)}
                        style={{ padding: '5px 10px', background: '#fff7ed', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#ea580c', fontSize: 12, fontWeight: 600 }}>
                        🔒 Force Close
                      </button>
                    )}
                    {s.status === 'in_progress' && can('route_planner','optimize') && (
                      <button onClick={() => reoptimizeSession(s.id)}
                        style={{ padding: '5px 10px', background: '#eef2ff', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#4f46e5', fontSize: 12, fontWeight: 600 }}>
                        ⟳ Re-optimize
                      </button>
                    )}
                    {s.status === 'completed' && can('driver_reports','edit') && <button onClick={() => openTimeModal(s)} title="Adjust start/close time"
                      style={{ padding: '5px 10px', background: '#eff6ff', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#0284c7', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                      🕐 Adjust time
                    </button>}
                    {can('invoices','delete') && <button onClick={() => openOdoEdit(s)} title="Record start/end odometer readings for this driver's route"
                      style={{ padding: '5px 10px', background: '#f0fdf4', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#16a34a', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                      🚗 Odometer
                    </button>}
                  </div>
                </td>
              </tr>
            ))}
            {!sessions.length && (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#94a3b8' }}>No sessions yet</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* View invoices modal */}
      {viewSession && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 800, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>{viewSession.driver_name} — {new Date(viewSession.session_date).toLocaleDateString()}</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{sessionInvoices.length} invoices · AED {Number(viewSession.total_value||0).toLocaleString()}</div>
              </div>
              <button onClick={() => setViewSession(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} color="#64748b" /></button>
            </div>
            <div style={{ overflow: 'auto', flex: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
                    {['Stop','Posting Date','Invoice No.','Customer','Code','Salesman','Value','Status','Action','Delivered At','Time Spent'].map(h => (
                      <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 500, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sessionInvoices.sort((a,b) => (a.stop_order||999)-(b.stop_order||999)).map(inv => (
                    <tr key={inv.id} style={{ borderTop: '1px solid #f8fafc' }}>
                      <td style={{ padding: '10px 14px', color: '#475569', fontWeight: 600 }}>{inv.stop_order || '—'}</td>
                      <td style={{ padding: '10px 14px', color: '#475569', whiteSpace: 'nowrap' }}>
                        {inv.posting_date ? new Date(inv.posting_date).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 600, color: '#0284c7' }}>
                        {inv.invoice_number || inv.notes || '—'}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#1e293b', fontWeight: 500 }}>{inv.customer_name}</td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>{inv.customer_code}</td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>{inv.salesman_name}</td>
                      <td style={{ padding: '10px 14px', color: '#1e293b' }}>AED {Number(inv.invoice_value||0).toLocaleString()}</td>
                      <td style={{ padding: '10px 14px' }}>
                        {(() => {
                          const ds = inv.delivery_status || inv.status;
                          const map = {
                            delivered: { bg: '#dcfce7', color: '#16a34a', label: '✅ Delivered' },
                            cancelled: { bg: '#fee2e2', color: '#dc2626', label: '❌ Cancelled' },
                            rescheduled: { bg: '#ede9fe', color: '#7c3aed', label: '📅 Rescheduled' },
                            pending: { bg: '#fef9c3', color: '#854d0e', label: '⏳ Pending' },
                            started: { bg: '#dbeafe', color: '#1d4ed8', label: '🚚 In Progress' },
                            reached: { bg: '#dbeafe', color: '#1d4ed8', label: '📍 Reached' },
                          };
                          const s = map[ds] || { bg: '#f1f5f9', color: '#475569', label: ds };
                          return <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: s.bg, color: s.color }}>{s.label}</span>;
                        })()}
                      </td>
                      <td style={{ padding: '10px 14px' }}>
                        {inv.delivery_status === 'cancelled' && (
                          <button onClick={() => openAdminReschedule(inv)}
                            style={{ padding: '4px 10px', background: '#ede9fe', border: 'none', borderRadius: 6, color: '#7c3aed', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                            📅 Reschedule
                          </button>
                        )}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#475569', fontSize: 12 }}>
                        {inv.delivered_at ? new Date(inv.delivered_at).toLocaleTimeString() : '—'}
                      </td>
                      <td style={{ padding: '10px 14px', color: '#475569' }}>
                        {inv.time_spent ? `${inv.time_spent} min` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {odoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 4 }}>🚗 Edit Odometer Reading</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>{odoModal.driver_name} — {new Date(odoModal.session_date).toLocaleDateString()}</div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Start reading (KM)</label>
            <input type="number" value={odoStart} onChange={e => setOdoStart(e.target.value)}
              placeholder="e.g. 45230"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }} />
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>End reading (KM)</label>
            <input type="number" value={odoEnd} onChange={e => setOdoEnd(e.target.value)}
              placeholder="e.g. 45430"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }} />
            {odoStart && odoEnd && parseInt(odoEnd) > parseInt(odoStart) && (
              <div style={{ fontSize: 12, color: '#16a34a', marginBottom: 14 }}>
                ✅ Distance: {parseInt(odoEnd) - parseInt(odoStart)} km
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setOdoModal(null)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={saveOdo} style={{ flex: 1, padding: 10, background: '#16a34a', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {timeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 4 }}>🕐 Adjust Session Time</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>{timeModal.driver_name} — {new Date(timeModal.session_date).toLocaleDateString()}</div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Started</label>
            <input type="datetime-local" value={newStartedAt} onChange={e => setNewStartedAt(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }} />
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Closed</label>
            <input type="datetime-local" value={newClosedAt} onChange={e => setNewClosedAt(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 14, boxSizing: 'border-box' }} />
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Reason (required)</label>
            <input value={timeReason} onChange={e => setTimeReason(e.target.value)} placeholder="e.g. driver forgot to close route at night"
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 8, boxSizing: 'border-box' }} />
            <div style={{ fontSize: 12, color: '#92400e', marginBottom: 14 }}>
              This recalculates duration from the new times and is logged for audit.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setTimeModal(null)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={submitTimeChange} style={{ flex: 1, padding: 10, background: '#0284c7', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {rescheduleModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 400, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 4 }}>📅 Reschedule Delivery</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>{rescheduleModal.customer_name} — cancelled, customer requested redelivery</div>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>New delivery date</label>
            <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 20, boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setRescheduleModal(null)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={confirmAdminReschedule} style={{ flex: 1, padding: 10, background: '#7c3aed', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Confirm</button>
            </div>
          </div>
        </div>
      )}
      {bcOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 620, maxHeight: '85vh', overflowY: 'auto', boxSizing: 'border-box', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b', marginBottom: 4 }}>🔄 Pull from Business Central</div>

            {bcStep === 'date' && (
              <>
                <div style={{ fontSize: 13, color: '#64748b', margin: '4px 0 18px' }}>Choose the posting date to pull invoices for.</div>
                <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>{bcRange ? 'From' : 'Posting date'}</label>
                <input type="date" value={bcFrom} onChange={e => setBcFrom(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 10, boxSizing: 'border-box' }} />
                {bcRange && (
                  <>
                    <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>To</label>
                    <input type="date" value={bcTo} min={bcFrom} onChange={e => setBcTo(e.target.value)}
                      style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, marginBottom: 10, boxSizing: 'border-box' }} />
                  </>
                )}
                <label style={{ fontSize: 12, color: '#0369a1', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 20 }}>
                  <input type="checkbox" checked={bcRange} onChange={e => { setBcRange(e.target.checked); if (e.target.checked) setBcTo(bcFrom); }} />
                  Use a date range
                </label>
                <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 14, marginBottom: 20 }}>
                  <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 6 }}>Add these to the plan dated</label>
                  <input type="date" value={bcPlanDate} onChange={e => setBcPlanDate(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
                  <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 5 }}>Defaults to today, regardless of the posting date above — change this only if you're building a future day's plan.</div>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setBcOpen(false)} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  <button onClick={bcFindSalespeople} disabled={bcLoading}
                    style={{ flex: 1, padding: 10, background: '#0369a1', border: 'none', borderRadius: 8, color: '#fff', cursor: bcLoading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {bcLoading ? 'Looking...' : 'Find Invoices'}
                  </button>
                </div>
              </>
            )}

            {bcStep === 'mapping' && (
              <>
                <div style={{ fontSize: 13, color: '#64748b', margin: '4px 0 14px' }}>
                  {bcSalespeople.reduce((n, s) => n + s.invoices, 0)} invoices from {bcFrom}{bcRange ? ` to ${bcTo}` : ''}, going into the {bcPlanDate} plan. Map each salesperson to a driver, or click a name to pick individual invoices.
                </div>
                <div style={{ maxHeight: 380, overflowY: 'auto', marginBottom: 18 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ textAlign: 'left', color: '#94a3b8', fontSize: 11 }}>
                        <th style={{ padding: '6px 4px' }}>Salesperson</th>
                        <th style={{ padding: '6px 4px' }}>Invoices</th>
                        <th style={{ padding: '6px 4px' }}>Driver</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bcSalespeople.map(s => (
                        <React.Fragment key={s.salesperson}>
                          <tr style={{ borderTop: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '8px 4px', fontWeight: 500, color: '#1e293b', cursor: 'pointer' }} onClick={() => bcToggleExpand(s.salesperson)}>
                              {bcExpanded === s.salesperson ? '▾' : '▸'} {s.salesperson}
                            </td>
                            <td style={{ padding: '8px 4px', color: '#64748b' }}>{s.invoices}</td>
                            <td style={{ padding: '8px 4px' }}>
                              <select value={bcAssignments[s.salesperson] || ''} onChange={e => setBcAssignments({ ...bcAssignments, [s.salesperson]: e.target.value })}
                                style={{ width: '100%', padding: '6px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }}>
                                <option value="">Skip</option>
                                {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                              </select>
                            </td>
                          </tr>
                          {bcExpanded === s.salesperson && (
                            <tr>
                              <td colSpan={3} style={{ padding: '0 4px 12px 20px', background: '#f8fafc' }}>
                                {bcExpandLoading && !bcInvoices[s.salesperson] ? (
                                  <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>Loading invoices...</div>
                                ) : (
                                  <React.Fragment>
                                    {(bcInvoices[s.salesperson] || []).length > 0 && (
                                      <div style={{ display: 'flex', gap: 14, padding: '2px 0 8px' }}>
                                        <button type="button" onClick={() => bcToggleAllForSalesperson(s.salesperson, true)}
                                          style={{ background: 'none', border: 'none', color: '#0369a1', cursor: 'pointer', padding: 0, fontSize: 11.5, textDecoration: 'underline' }}>Select all</button>
                                        <button type="button" onClick={() => bcToggleAllForSalesperson(s.salesperson, false)}
                                          style={{ background: 'none', border: 'none', color: '#0369a1', cursor: 'pointer', padding: 0, fontSize: 11.5, textDecoration: 'underline' }}>Deselect all</button>
                                      </div>
                                    )}
                                    {(bcInvoices[s.salesperson] || []).map(inv => (
                                      <label key={inv.invoice_no} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 12, color: inv.state === 'already_here' ? '#cbd5e1' : '#334155' }}>
                                        <input type="checkbox" disabled={inv.state === 'already_here'}
                                          checked={inv.state !== 'already_here' && !bcExcluded[inv.invoice_no]}
                                          onChange={() => bcToggleInvoice(inv.invoice_no)} />
                                        <span style={{ fontWeight: 600 }}>{inv.invoice_no}</span>
                                        <span>{inv.customer_name}</span>
                                        <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>
                                          {inv.state === 'missing_coords' ? 'needs location' : inv.state === 'already_here' ? inv.why : ''}
                                        </span>
                                      </label>
                                    ))}
                                  </React.Fragment>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setBcStep('date')} style={{ flex: 1, padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Back</button>
                  <button onClick={bcImport} disabled={bcLoading}
                    style={{ flex: 1, padding: 10, background: '#16a34a', border: 'none', borderRadius: 8, color: '#fff', cursor: bcLoading ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600 }}>
                    {bcLoading ? 'Importing...' : 'Import'}
                  </button>
                </div>
              </>
            )}

            {bcStep === 'coords' && (
              <>
                <div style={{ fontSize: 13, color: '#64748b', margin: '4px 0 14px' }}>
                  Imported. {bcNeedsLocation.length} customer{bcNeedsLocation.length === 1 ? '' : 's'} need a delivery location before their invoice shows on the map.
                </div>
                <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 18 }}>
                  {bcNeedsLocation.map(cust => (
                    <div key={cust.customer_code} style={{ border: '1px solid #f1f5f9', borderRadius: 8, padding: 12 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>{cust.customer_name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>{cust.customer_code} · {cust.salesperson}{cust.address ? ` · ${cust.address}` : ''}</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input type="number" placeholder="Latitude" step="any"
                          value={bcCoords[cust.customer_code]?.lat || ''}
                          onChange={e => setBcCoords({ ...bcCoords, [cust.customer_code]: { ...bcCoords[cust.customer_code], lat: e.target.value } })}
                          style={{ flex: 1, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
                        <input type="number" placeholder="Longitude" step="any"
                          value={bcCoords[cust.customer_code]?.lng || ''}
                          onChange={e => setBcCoords({ ...bcCoords, [cust.customer_code]: { ...bcCoords[cust.customer_code], lng: e.target.value } })}
                          style={{ flex: 1, padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12 }} />
                        <button onClick={() => bcSaveLocation(cust)}
                          style={{ padding: '8px 14px', background: '#0369a1', border: 'none', borderRadius: 6, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Save</button>
                      </div>
                    </div>
                  ))}
                  {!bcNeedsLocation.length && <div style={{ color: '#16a34a', fontSize: 13 }}>✅ All customers have a location.</div>}
                </div>
                <button onClick={() => setBcOpen(false)} style={{ width: '100%', padding: 10, background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Done</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
